import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import debounce from 'lodash/debounce';
import gettext from '@eb/gettext';
import loadable from '@loadable/component';

import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';
import { MapPinFill as MapPinFillSvg } from '@eb/eds-iconography';
import { ArrowRightChunky as ArrowRightChunkySvg } from '@eb/eds-iconography';

import { addWindowListener, removeWindowListener } from '@eb/eds-utils';
import { HAS_WINDOW } from '@eb/feature-detection';

import { storeQuery } from '@eb/redux-destination';
import { CATEGORY_DROPDOWN_SELECT } from '../../constants';
import {
    SUGGESTIONS_PROPTYPE,
    EVENTS_TYPE,
    ALL_EVENTS_TYPE,
} from '../../../constants';

import './searchInput.scss';

const SearchAutocomplete = loadable(() => import('./SearchAutocomplete'));
const ConnectedLocationAutocomplete = loadable(() =>
    import('../../containers/ConnectedLocationAutocomplete'),
);

const LOCATION_DEBOUNCE_INTERVAL = 200;
const PAGE_SHOW_EVENT_NAME = 'pageshow';

export default class SearchInput extends PureComponent {
    static propTypes = {
        handleApplyFilter: PropTypes.func.isRequired,
        handleLocationSuggestionChange: PropTypes.func.isRequired,
        handleSearchSuggestionChange: PropTypes.func.isRequired,
        currentPlace: PropTypes.string,
        initialSearchQuery: PropTypes.string,
        suggestions: SUGGESTIONS_PROPTYPE,
        handleLocationSelect: PropTypes.func,
        handleUseCurrentLocation: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.initialSearchQuery,
            shouldFocusInput: false,
        };

        this._boundHandlePageLoadFromCache = this._handlePageLoadFromCache;
    }

    componentDidMount() {
        addWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        initialSearchQuery: newInitialSearchQuery,
    }) {
        if (newInitialSearchQuery !== this.props.initialSearchQuery) {
            this.setState({ searchQuery: newInitialSearchQuery });
        }
    }

    componentWillUnmount() {
        removeWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._boundHandlePageLoadFromCache,
        );
    }

    /**
     * Helper function which consumes the `pageshow` event to determine if the
     * page was served from the browser cache. Allows for a hook such that any
     * state resetting or other behaviors can occur.
     */
    _handlePageLoadFromCache = (event) => {
        const { currentPlace, initialSearchQuery } = this.props;

        if (event.persisted) {
            this.setState({
                searchQuery: initialSearchQuery,
            });

            //For browsers which utilize back-forward cache, the store is cached with the
            //autocomplete suggestions previously entered by the user so we want to display
            //suggestions which make sense in the context of the current state.
            this._requestLocationSuggestions(currentPlace);
        }
    };

    _handleSubmit = () => {
        const { handleApplyFilter, hasHistory } = this.props;

        let { searchQuery } = this.state;

        let newQuery = searchQuery;

        if (hasHistory && HAS_WINDOW) {
            storeQuery(searchQuery);
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], searchQuery)) {
            newQuery = '';
        }

        // when searching term matches category dropdown input and query should be resetted and
        if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
            searchQuery = '';
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => {
                handleApplyFilter({ q: searchQuery });
            },
        );
    };

    _handleSearchSuggestionChange = (searchQuery) => {
        const { handleSearchSuggestionChange } = this.props;

        handleSearchSuggestionChange(searchQuery);
    };

    _handleApplySearchFilter = (searchFilter) => {
        const { handleApplyFilter } = this.props;
        const { searchQuery } = this.state;

        let newQuery = searchQuery;
        const { q, tags } = searchFilter;

        if (q) {
            newQuery = q;
        }

        // Special case where the user selects a category from the
        // dropdown. Need to ensure a re-render occurs so utilizing
        // an allow-listed value to pass through.
        if (q === '' && tags) {
            newQuery = CATEGORY_DROPDOWN_SELECT;

            if (searchQuery === CATEGORY_DROPDOWN_SELECT) {
                newQuery = '';
            }
        }

        // when searching for 'events' or 'all events' input should be resetted
        if (includes([EVENTS_TYPE, ALL_EVENTS_TYPE], (q || '').toLowerCase())) {
            newQuery = '';
        }

        this.setState(
            {
                searchQuery: newQuery,
            },
            () => handleApplyFilter(searchFilter),
        );
    };

    _handleSearchQueryChange = (searchQuery) => {
        this.setState({ searchQuery });
    };

    _handleSearchFieldFocus = () => {
        this.setState({
            shouldFocusInput: true,
        });
    };

    _handleSearchFieldBlur = () => {
        this.setState({
            shouldFocusInput: false,
        });
    };

    _handleClearButtonClick = () => {
        this.setState({
            searchQuery: '',
            shouldFocusInput: true,
        });
    };

    _requestLocationSuggestions = debounce((locationQuery) => {
        const { handleLocationSuggestionChange } = this.props;

        handleLocationSuggestionChange(locationQuery);
    }, LOCATION_DEBOUNCE_INTERVAL);
    render() {
        const {
            suggestions,
            hasHistory,
            trackGAEvent,
            handleUseCurrentLocation,
            handleLocationSelect,
            onGoogleMapsLoaded,
            history,
        } = this.props;
        const { searchQuery, shouldFocusInput } = this.state;

        const mapIcon = (
            <Icon color="grey-800" size="small" type={<MapPinFillSvg />} />
        );

        return (
            <form autoComplete="none">
                <nav className="search-input">
                    <div className="search-input__query">
                        <div className="search-input__query__container">
                            <SearchAutocomplete
                                hasHistory={hasHistory}
                                searchQuery={searchQuery}
                                suggestions={suggestions}
                                onSearchSuggestionChange={
                                    this._handleSearchSuggestionChange
                                }
                                onSearchQueryChange={
                                    this._handleSearchQueryChange
                                }
                                onSearchFilterChange={
                                    this._handleApplySearchFilter
                                }
                                onClearButtonClick={
                                    this._handleClearButtonClick
                                }
                                onFocus={this._handleSearchFieldFocus}
                                onBlur={this._handleSearchFieldBlur}
                                shouldFocusInput={shouldFocusInput}
                                trackGAEvent={trackGAEvent}
                                history={history}
                            />
                            <ConnectedLocationAutocomplete
                                handleUseCurrentLocation={
                                    handleUseCurrentLocation
                                }
                                handleLocationSelect={handleLocationSelect}
                                onGoogleMapsLoaded={onGoogleMapsLoaded}
                                history={history}
                                prefix={mapIcon}
                            />
                        </div>

                        <div className="eds-l-mar-bot-5 eds-show-up-sw">
                            <Button
                                data-spec="search-button"
                                onClick={this._handleSubmit}
                                style="neutral"
                                type="button"
                            >
                                {gettext('Search')}
                            </Button>
                        </div>
                        <div className="eds-l-mar-bot-5 eds-show-down-sm">
                            <Button
                                style="neutral"
                                type="button"
                                onClick={this._handleSubmit}
                                __containerClassName="search-input__query-submit"
                            >
                                <Icon
                                    type={<ArrowRightChunkySvg />}
                                    strokeColor="ui-800"
                                />
                            </Button>
                        </div>
                    </div>
                    <div className="search-input__divider eds-l-mar-2" />
                </nav>
            </form>
        );
    }
}
